'use client';

import { ConfigProvider, Layout, Menu, theme, MenuProps } from 'antd';
import { useStore } from '@/store/zustand/store-zustand';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import ArrowLeft from '@/icons/arrowLeft';
import ArrowRight from '@/icons/arrowRight';
import SvgComponent from '@/icons/sandinas';
import image from 'public/images/undefined.png';
import { CSSProperties, useMemo } from 'react';
import { hasPermission } from '@/utils/services/security';
import { modalError } from '@/utils/ui/wrapper/NotificationWrapper';
import { dsTheme } from '@/styles/dsTheme';

// Nombre de un icono de Material Symbols Rounded (02_lineamientos_visuales.md #2), el mismo
// mapeo que ya usa el Dashboard (AB#10842) para que la seccion de navegacion y las cards de
// acceso rapido se vean consistentes.
const Icon = (name: string) => <span className="material-symbols-rounded">{name}</span>;



const { Sider } = Layout;

const SIDEBAR_HEADER_HEIGHT = '64px';
// Alto real del bloque "Colapsar" (trigger del Sider) -- usado tanto en el override de
// Layout.triggerHeight como en menuStyle mas abajo, para que no puedan volver a desincronizarse
// (antes menuStyle restaba un "65px" a mano que no coincidia con el trigger, AB#10842).
const SIDEBAR_TRIGGER_HEIGHT = 64;
// Alto del bloque de version, al pie del Sidebar, entre el Menu y el trigger "Colapsar" (AB#11018).
// El trigger es position:fixed (ver comentario de collapseTriggerStyle mas abajo): se renderiza
// como su propio overlay con fondo solido, por fuera del flujo normal del <Sider>. menuStyle ya
// reservaba exactamente SIDEBAR_TRIGGER_HEIGHT de alto libre debajo del Menu para ese overlay; sin
// reservar tambien el alto de este bloque, el div de version caia en flujo normal justo despues del
// Menu -- dentro de la misma franja que el trigger fijo tapa con su fondo -- y quedaba invisible
// detras suyo aunque el texto se renderizara. Mismo mecanismo que ya usa SIDEBAR_TRIGGER_HEIGHT:
// una sola constante para el alto real y el hueco reservado, para que no puedan desincronizarse.
const SIDEBAR_VERSION_HEIGHT = 28;

const DashboardSidebar = () => {
  const { token } = theme.useToken();
  const fullPath = usePathname();
  const { collapsed, toggleCollapsed } = useStore();

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: SIDEBAR_HEADER_HEIGHT,
    width: '90%',
    background: token.colorBgContainer,
    color: token.colorText,
  };

  // Bloque "Colapsar" al pie del Sidebar (AB#10842, calcado de ejemploSidebar.png): antes solo
  // se renderizaba la flecha, sin texto ni linea divisoria -- la referencia lo muestra como su
  // propio bloque, separado del menu por un borde superior (#E5E5E5, mismo gris que las demas
  // lineas del rollout), con el icono + "Colapsar" alineados al inicio, igual que el resto de
  // los items del Sidebar (paddingLeft 24, igual al de los items del Menu). Cuando el Sidebar
  // esta colapsado se centra (no hay texto ni espacio para mantener el alineado a la izquierda),
  // mismo criterio que ya aplica el Menu de antd a sus propios items. El texto se oculta en ese
  // estado (no entra en el ancho colapsado).
  const collapseTriggerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: 12,
    height: SIDEBAR_HEADER_HEIGHT,
    width: '100%',
    paddingLeft: collapsed ? 0 : 24,
    boxSizing: 'border-box',
    background: token.colorBgContainer,
    borderTop: '1px solid #E5E5E5',
    // El trigger de Sider es position:fixed -- se renderiza como su propio overlay, no como
    // parte del box del <Sider>, asi que tapa el tramo de abajo del borderRight del Sider
    // (siderStyle) con su propio fondo solido. Se repite el mismo borde aca para que la linea
    // vertical se vea continua hasta el pie (AB#10842).
    borderRight: '1px solid #E5E5E5',
    // Color calcado por muestreo de pixeles de ejemploSidebar.png -- distinto del gris de los
    // items del menu (mas azulado), consistente con tratarse de una accion secundaria.
    color: '#8C9ABC',
    fontSize: 14,
    cursor: 'pointer',
  };

  const siderStyle: CSSProperties = {
    background: token.colorBgContainer,
    height: '100vh',
    overflowY: 'auto',
    // Sin overflowX explicito, CSS lo computa como 'auto' igual que overflowY (regla del spec de
    // overflow: si un eje no es 'visible', el otro deja de ser 'visible' tambien). Eso habilitaba
    // un scroll horizontal que se veia un instante durante la animacion de colapsar/expandir el
    // Sidebar, mientras el Menu todavia estaba reflowando sus labels (AB#10842).
    overflowX: 'hidden',
    // Linea divisoria vertical entre el Sidebar y el Navbar/Contenido (AB#10842): #E5E5E5
    // calcado por muestreo de pixeles de la imagen de referencia del usuario (ejemploPantalla.png,
    // 693x88px -- muy chico para leerlo a ojo, se sampleo con GetPixel sobre un zoom nearest-
    // neighbor). Mismo gris que el borde inferior del Navbar para que ambas lineas se vean como
    // una sola, continua, en el punto donde se cruzan.
    borderRight: '1px solid #E5E5E5',
  };

  const menuStyle: CSSProperties = {
    height: `calc(100vh - ${SIDEBAR_HEADER_HEIGHT} - ${SIDEBAR_TRIGGER_HEIGHT}px - ${SIDEBAR_VERSION_HEIGHT}px)`,
    overflowY: 'auto',
    overflowX: 'hidden',
  };

  const [selectedKeys, openKeys] = useMemo(() => {
    const paths = fullPath.split('/');
    const selected =
      paths.length > 2
        ? [paths[paths.length - 2], paths[paths.length - 1]]
        : [paths[paths.length - 1]];

    const open = paths.length >= 2 ? [paths[paths.length - 2]] : selected;
    return [selected, open];
  }, [fullPath]);

  const withLocalePath = (destination: string) => `/${destination}`;

  const getMenuItem = (
    key: string,
    icon: React.ReactNode,
    label: string,
    href: string
  ) => {
    const needsTooltip = label.length > 20; // Muestra tooltip si el texto es largo
    return {
      key,
      icon,
      label: <Link href={withLocalePath(href)}>{label}</Link>,
      title: needsTooltip ? label : undefined, // Tooltip solo si el label es largo
    };
  };

  const menuItems: MenuProps['items'] = useMemo(() => {
    const filteredSections = [
      {
        type: 'group' as const, // Aseguramos que sea el literal "group"
        label: '',
        children: [
          getMenuItem('dashboard', Icon('home'), 'Dashboard', 'dashboard'),
        ].filter(Boolean),
      },
      {
        type: 'group' as const,
        // Mayusculas literales (AB#10842, calcado de ejemploSidebar.png) en vez de un
        // textTransform: la referencia muestra las 3 etiquetas de grupo siempre en caps.
        // Vacio cuando el Sidebar esta colapsado: antd no oculta el titulo de grupo por si solo
        // (a diferencia del label de cada item, que si se esconde), asi que quedaba visible/
        // amontonado en el ancho angosto.
        label: collapsed ? '' : 'SOLICITUDES',
        children: [
          hasPermission('SolicitudesRLVer') &&
          getMenuItem(
            'solicitudes',
            Icon('person'),
            'Personas Físicas',
            'solicitudes'
          ),
          hasPermission('SolicitudesJuridicasVer') &&
          getMenuItem(
            'solicitudesJur',
            Icon('domain'),
            'Personas Jurídicas',
            'solicitudesJur'
          ),
        ].filter(Boolean),
      },
      {
        type: 'group' as const,
        label: collapsed ? '' : 'CONFIGURACIÓN',
        children: [
          hasPermission('EntidadesVer') &&
          getMenuItem(
            'entidades',
            Icon('layers'),
            'Entidades',
            'entidades'
          ),
          hasPermission('ContratoVer') &&
          getMenuItem(
            'contrato',
            Icon('description'),
            'Contratos',
            'contrato'
          ),
          // "Listas" en la referencia es un unico item -- pero Lista Blanca y Lista Negra son
          // paginas/permisos reales distintos (listaBlanca / listaNegra), no hay una ruta
          // combinada a la que apuntar sin inventarla. Se dejan como 2 items separados,
          // agrupados juntos en CONFIGURACION igual que en la referencia, con iconos de la
          // misma familia "lista" en vez de los thumb_up/thumb_down anteriores.
          hasPermission('ListaBlancaVer') &&
          getMenuItem(
            'listaBlanca',
            Icon('checklist'),
            'Lista Blanca',
            'listaBlanca'
          ),
          hasPermission('ListaNegraVer') &&
          getMenuItem(
            'listaNegra',
            Icon('playlist_remove'),
            'Lista Negra',
            'listaNegra'
          ),
          hasPermission('FlujoVer') &&
          getMenuItem('flujo', Icon('account_tree'), 'Flujos', 'flujo'),
          hasPermission('NivelAprobacionVer') &&
          getMenuItem(
            'nivelAprobacion',
            Icon('stairs'),
            'Niveles de aprobación',
            'nivelAprobacion'
          ),
          hasPermission('ParametrosVer') &&
          getMenuItem(
            'parametros',
            Icon('settings_suggest'),
            'Parámetros',
            'parametros'
          ),
        ].filter(Boolean),
      },
      {
        type: 'group' as const,
        label: collapsed ? '' : 'SEGURIDAD',
        children: [
          hasPermission('AuditoriasVer') &&
          getMenuItem(
            'auditorias',
            Icon('shield'),
            'Auditorías',
            'auditorias'
          ),
          hasPermission('UsuariosVer') &&
          getMenuItem('usuario', Icon('manage_accounts'), 'Usuarios', 'usuario'),
          hasPermission('PerfilesVer') &&
          getMenuItem('perfil', Icon('contact_page'), 'Perfiles', 'perfil'),
        ].filter(Boolean),
      },
    ];

    // Filtra las secciones que tienen al menos un elemento en children
    return filteredSections.filter((section) => section.children.length > 0);
  }, [collapsed]);

  // Texto fijo "V 2.0.1" al pie del Sidebar (AB#11018), en el mismo lugar donde antes se
  // mostraba un guion suelto como placeholder del fetch a /version (AB#10842).
  const version = 'V 2.0.1';

  return (
    // Design System Soluciones Andinas (02_lineamientos_visuales.md, AB#10842): el sidebar es
    // infra compartida por toda la app (se monta en cada pagina), asi que a diferencia de las
    // paginas puntuales que ya migramos este cambio es global -- mismo criterio que ya se aplico
    // para triggerBg/hover en AB#10838/10841.
    <ConfigProvider
      theme={{
        ...dsTheme,
        components: {
          // Ant Design le pinta un fondo oscuro fijo (#002140, token Layout.triggerBg) al
          // contenedor del trigger de colapso sin importar el tema de la app; sin este override
          // se ve como un bloque negro asomando debajo del contenido custom (mismo headerStyle
          // que el logo de arriba). triggerHeight (default 48px) tambien se pisa para que
          // coincida con la altura de collapseTriggerStyle (64px, SIDEBAR_HEADER_HEIGHT) -- sin
          // esto el contenedor de antd recortaba nuestro div (borde superior y centrado del
          // texto "Colapsar" quedaban cortados/desalineados) (AB#10842).
          Layout: {
            triggerBg: token.colorBgContainer,
            triggerColor: token.colorText,
            triggerHeight: SIDEBAR_TRIGGER_HEIGHT,
          },
          // Hover de las secciones del menu alineado al Design System (AB#10842): reemplaza el
          // tinte crema/hueso de AB#10841 (pedido antes de tener el DS completo) por un tinte del
          // primario de marca (#1798D5), el mismo criterio que ya usan los icon chips de
          // SectionHeader/Dashboard (rgba(23,152,213,0.10)). color-mix() lo deriva del fondo real
          // del sidebar (colorBgContainer) para que funcione igual en tema claro y oscuro sin
          // hardcodear dos hex.
          Menu: {
            itemHoverBg: `color-mix(in srgb, ${token.colorBgContainer} 92%, #1798D5 8%)`,
            itemHoverColor: '#1798D5',
            // Colores calcados por muestreo de pixeles de ejemploSidebar.png (AB#10842):
            // fondo/color del item seleccionado (#EAF7FD / #1798D5, iguales a los de la pildora
            // de entidad del Navbar) y color de los titulos de grupo (#CBD5E1, mas claro que el
            // #8C8C8C generico que se usa en el resto del DS -- esta referencia puntual usa un
            // gris con tinte azul distinto, se prioriza el pixel real sobre el token generico).
            itemSelectedBg: '#EAF7FD',
            itemSelectedColor: '#1798D5',
            // itemColor (gris de los items sin seleccionar) es una estimacion: el sampleo de
            // pixeles de "Dashboard"/"Entidades" no dio un hex limpio por el antialiasing
            // subpixel (ClearType) del texto en la captura original -- #475569 es un gris slate
            // razonable, no un valor confirmado pixel a pixel como los de arriba.
            itemColor: '#475569',
            itemBorderRadius: 12,
            groupTitleColor: '#CBD5E1',
            groupTitleFontSize: 11.5,
            // Alineacion del titulo de grupo con el icono de sus items (AB#10842): antd no
            // expone un token de padding para .ant-menu-item-group-title, asi que en vez de
            // adivinar su valor default se fija itemPaddingInline a un numero propio y se pisa
            // el padding del titulo de grupo (mas abajo, <style jsx global>) al mismo valor --
            // asi quedan alineados por definicion, no por coincidencia con un default de antd.
            itemPaddingInline: 24,
          },
        },
      }}
    >
      {/* .ant-menu-item-group-title no tiene un token de padding en antd (a diferencia de
          itemPaddingInline, que si es overrideable arriba) -- se pisa aca al mismo valor (24px)
          para que el titulo de cada grupo quede alineado con el icono de sus items (AB#10842,
          calcado de ejemploSidebar.png). */}
      <style jsx global>{`
        .ant-menu-item-group-title {
          padding-inline-start: 24px !important;
        }
      `}</style>
      <Sider
        breakpoint="lg"
        collapsible
        collapsed={collapsed}
        // Ancho default de antd (200px) le quedaba corto a labels largos ("Solicitudes
        // Jurídicas", "Nivel de aprobación") y los cortaba con ellipsis -- de ahi el mecanismo de
        // tooltip para labels de mas de 20 caracteres (ver needsTooltip). 240px es el minimo del
        // que dejan de necesitar el tooltip. collapsedWidth por defecto (80) no cambia.
        width={240}
        style={siderStyle}
        trigger={
          <div onClick={toggleCollapsed} style={collapseTriggerStyle}>
            {!collapsed ? <ArrowLeft /> : <ArrowRight />}
            {!collapsed && <span>Colapsar</span>}
          </div>
        }
      >
        <div
          style={{
            ...headerStyle,
            // Linea divisoria horizontal (AB#10842): mismo gris y misma altura que el borde
            // inferior del Navbar, para que ambas formen una sola linea continua. width 100%
            // (headerStyle trae 90%, pensado para centrar el logo) evita que la linea se corte
            // antes de llegar al borde derecho del Sidebar. box-sizing border-box evita que el
            // borde sume 1px extra a la altura fija del header.
            width: '100%',
            borderBottom: '1px solid #E5E5E5',
            boxSizing: 'border-box',
          }}
        >
          {/* <SvgComponent /> */}
          <img
            src={image.src}
            alt="Logo"
            style={{
              maxWidth: '80%',
              maxHeight: '80%',
              width: 'auto',
              height: 'auto',
            }}
          />
        </div>
        <Menu
          mode="inline"
          style={menuStyle}
          selectedKeys={selectedKeys}
          openKeys={!collapsed ? openKeys : undefined}
          items={menuItems}
        />

        <div
          style={{
            height: SIDEBAR_VERSION_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontSize: '12px', color: token.colorText }}>{version}</span>
        </div>
      </Sider>
    </ConfigProvider>
  );
};

export default DashboardSidebar;
